// pricingController.js - Admin CRM pricing page: App Store prices (globals/pricing_matrix, from
// syncAppStorePricing*) beside Google Play's (globals/play_pricing_matrix, from syncPlayPricing*).
import { BADGE } from "./ui/classes.js";

// Tailwind class lists for the table (#1211); whole strings, so Tailwind finds them.
const PRICE = "px-3 py-3 text-right text-sm whitespace-nowrap text-gray-900 tabular-nums dark:text-white";
const PRICE_LAST = "py-3 pr-4 pl-3 text-right text-sm whitespace-nowrap text-gray-900 tabular-nums sm:pr-6 dark:text-white";
const PRICE_DIFF = "px-3 py-3 text-right text-sm font-semibold whitespace-nowrap text-amber-700 tabular-nums dark:text-amber-400";
const PRICE_DIFF_LAST = "py-3 pr-4 pl-3 text-right text-sm font-semibold whitespace-nowrap text-amber-700 tabular-nums sm:pr-6 dark:text-amber-400";
const MISSING = "px-3 py-3 text-right text-sm text-gray-300 dark:text-gray-600";
const MISSING_LAST = "py-3 pr-4 pl-3 text-right text-sm text-gray-300 sm:pr-6 dark:text-gray-600";
const SUB = "block text-xs font-normal text-gray-500 dark:text-gray-400";

let appleCountries = [];
let playCountries = [];
let currentActiveRegion = "all";
let activeDependencies = null;

export async function loadPricingTemplate() {
  const containerEl = document.getElementById("sectionPricingContainer");
  if (!containerEl) return false;

  try {
    const res = await fetch("templates/pricing.html");
    if (!res.ok) throw new Error(`HTTP ${res.status} fetching pricing.html`);
    const html = await res.text();
    containerEl.innerHTML = html;
    return true;
  } catch (err) {
    console.error("❌ Failed to load pricing template:", err);
    return false;
  }
}

// Binds the page's controls. It reads nothing: the template is mounted before anyone has signed in,
// so app.js calls fetchAndRenderPricing once an admin is in (#1333).
export function initPricingController(deps) {
  activeDependencies = deps;
  const { functions, httpsCallable, showToast } = deps;

  const searchInput = document.getElementById("pricingSearchInput");
  if (searchInput) {
    searchInput.addEventListener("input", () => {
      filterAndRenderTable();
    });
  }

  // Region filter pill buttons
  const filterBtns = document.querySelectorAll(".pricing-filter-btn");
  filterBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      filterBtns.forEach((b) => b.setAttribute("aria-pressed", String(b === btn)));
      currentActiveRegion = btn.dataset.region || "all";
      filterAndRenderTable();
    });
  });

  document.getElementById("syncAppStorePricingBtn")?.addEventListener("click", () => handleSync({
    functions, httpsCallable, showToast,
    callable: "syncAppStorePricingManual",
    buttonId: "syncAppStorePricingBtn",
    textId: "syncBtnText",
    idleText: "Sync with App Store",
    busyText: "Syncing with Apple…",
    doneText: "App Store prices synced"
  }));

  document.getElementById("syncPlayPricingBtn")?.addEventListener("click", () => handleSync({
    functions, httpsCallable, showToast,
    callable: "syncPlayPricingManual",
    buttonId: "syncPlayPricingBtn",
    textId: "playSyncBtnText",
    idleText: "Sync with Google Play",
    busyText: "Syncing with Google Play…",
    doneText: "Google Play prices synced"
  }));
}

export async function fetchAndRenderPricing(db, doc, getDoc) {
  const read = async (id) => {
    try {
      const snap = await getDoc(doc(db, "globals", id));
      return snap.exists() ? snap.data() : null;
    } catch (err) {
      console.error(`Error fetching globals/${id}:`, err);
      return null;
    }
  };
  const [apple, play] = await Promise.all([read("pricing_matrix"), read("play_pricing_matrix")]);

  appleCountries = apple ? appleCountriesFrom(apple) : [];
  playCountries = Array.isArray(play?.countries) ? play.countries : [];

  updateAppleSummary(apple);
  updatePlaySummary(play);
  filterAndRenderTable();
}

function appleCountriesFrom(data) {
  if (Array.isArray(data.countries) && data.countries.length) return data.countries;
  // A legacy document has only pricing_matrix, keyed by country code and by currency.
  return Object.entries(data.pricing_matrix || {})
    .filter(([key, item]) => key === item.countryCode)
    .map(([, item]) => item);
}

function formatSyncTime(iso) {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return "never";
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function setText(id, text) {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
}

function updateAppleSummary(data) {
  setText("pricingLastSyncedText", data ? formatSyncTime(data.updated_at) : "never");
  setText("pricingSourceBadge", data ? (data.source === "app_store_connect_api" ? "App Store Connect API" : "StoreKit tiers") : "not synced yet");
  setText("statPricingMarketsCount", data ? String(data.country_count || appleCountries.length) : "–");

  const us = appleCountries.find((c) => c.countryCode === "US");
  setText("statPricingMonthlyUSD", us?.monthlyFormatted || "–");
  setText("statPricingAnnualUSD", us?.annualFormatted || "–");
  setText("statPricingSavings", typeof us?.savingsPercentage === "number" ? `${us.savingsPercentage}%` : "–");
}

function updatePlaySummary(data) {
  setText("playLastSyncedText", data ? formatSyncTime(data.updated_at) : "never");
  const plans = data
    ? [data.monthly_plan, data.annual_plan].filter(Boolean).map((p) => `${p.product_id}:${p.base_plan_id}`).join(", ")
    : "";
  const trial = data?.trial_formatted || (data ? "no free trial offer" : "");
  setText("playPlanText", data ? `${data.country_count} regions · ${trial}` : "not synced yet");
  const tag = document.getElementById("playPlanText");
  if (tag) tag.title = plans;
}

/**
 * One row per country either store sells in: `{ code, name, flag, region, topMarket, apple, play }`,
 * top markets first, then by name.
 */
export function mergeStorefronts(apple = [], play = []) {
  const rows = new Map();
  apple.forEach((c) => rows.set(c.countryCode, { code: c.countryCode, name: c.countryName, flag: c.flag, region: c.region, topMarket: Boolean(c.topMarket), apple: c, play: null }));
  play.forEach((c) => {
    const row = rows.get(c.countryCode);
    if (row) row.play = c;
    else rows.set(c.countryCode, { code: c.countryCode, name: c.countryName, flag: c.flag, region: c.region, topMarket: Boolean(c.topMarket), apple: null, play: c });
  });
  return [...rows.values()].sort((a, b) => (a.topMarket === b.topMarket ? String(a.name).localeCompare(String(b.name)) : a.topMarket ? -1 : 1));
}

function filterAndRenderTable() {
  const rows = mergeStorefronts(appleCountries, playCountries);
  setText("countAllMarkets", String(rows.length));
  setText("countTopMarkets", String(rows.filter((r) => r.topMarket).length));

  const query = (document.getElementById("pricingSearchInput")?.value || "").trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (currentActiveRegion === "top" && !r.topMarket) return false;
    if (currentActiveRegion !== "all" && currentActiveRegion !== "top" && r.region !== currentActiveRegion) return false;
    if (!query) return true;
    return [r.name, r.code, r.apple?.iso3, r.apple?.currencyCode, r.apple?.symbol, r.play?.monthlyCurrency, r.play?.annualCurrency]
      .some((v) => v && String(v).toLowerCase().includes(query));
  });

  renderPricingTableDOM(filtered, query, rows.length);
}

/** The Google Play price differs from the App Store's: same currency, different amount. */
export function playDiffers(appleAmount, appleCurrency, playAmount, playCurrency) {
  if (typeof appleAmount !== "number" || typeof playAmount !== "number") return false;
  return appleCurrency === playCurrency && Math.abs(appleAmount - playAmount) > 0.004;
}

function appleCell(price, sub) {
  if (!price) return `<td class="${MISSING}">—</td>`;
  return `<td class="${PRICE}">${escapeHTML(price)}${sub ? `<span class="${SUB}">${escapeHTML(sub)}</span>` : ""}</td>`;
}

// The last column ends the row, so it takes the row's right padding.
function playCell({ price, sub, available, differs, applePrice, last = false }) {
  if (!price) return `<td class="${last ? MISSING_LAST : MISSING}">—</td>`;
  const title = differs ? ` title="${escapeHTML(`App Store: ${applePrice}`)}"` : "";
  const closed = available === false ? `<span class="${SUB}">closed to new subscribers</span>` : "";
  const cls = differs ? (last ? PRICE_DIFF_LAST : PRICE_DIFF) : (last ? PRICE_LAST : PRICE);
  return `<td class="${cls}"${title}>${escapeHTML(price)}${sub ? `<span class="${SUB}">${escapeHTML(sub)}</span>` : ""}${closed}</td>`;
}

function yearlySub(perMonth, savings) {
  const parts = [];
  if (perMonth) parts.push(`${perMonth}/mo`);
  if (typeof savings === "number") parts.push(`save ${savings}%`);
  return parts.join(" · ");
}

export function renderPricingTableDOM(rows, searchQuery = "", total = rows.length) {
  const tbody = document.getElementById("pricingTableBody");
  if (!tbody) return;

  if (!rows || rows.length === 0) {
    tbody.innerHTML = `<tr><td colspan="6" class="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">${
      total === 0
        ? "No prices synced yet. Use <strong class=\"font-semibold text-gray-900 dark:text-white\">Sync with App Store</strong> or <strong class=\"font-semibold text-gray-900 dark:text-white\">Sync with Google Play</strong> above."
        : `No country matches "${escapeHTML(searchQuery)}".`
    }</td></tr>`;
    return;
  }

  tbody.innerHTML = rows.map((r) => {
    const a = r.apple;
    const p = r.play;
    const tier = r.topMarket
      ? `<span class="${BADGE.paprika}">Top market</span>`
      : `<span class="text-sm text-gray-500 dark:text-gray-400">Standard</span>`;
    return `
      <tr>
        <th scope="row" class="py-3 pr-3 pl-4 text-left text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-6 dark:text-white">
          <span class="mr-2" aria-hidden="true">${escapeHTML(r.flag || "🌐")}</span>${escapeHTML(r.name || r.code)}
          <span class="ml-1 font-mono text-xs font-normal text-gray-500 dark:text-gray-400">${escapeHTML(r.code)}</span>
        </th>
        <td class="px-3 py-3 whitespace-nowrap">${tier}</td>
        ${appleCell(a?.monthlyFormatted)}
        ${playCell({
          price: p?.monthlyFormatted,
          available: p?.monthlyAvailable,
          differs: Boolean(a && p) && playDiffers(a.monthlyPrice, a.currencyCode, p.monthlyPrice, p.monthlyCurrency),
          applePrice: a?.monthlyFormatted
        })}
        ${appleCell(a?.annualFormatted, a ? yearlySub(a.annualMonthlyFormatted, a.savingsPercentage) : "")}
        ${playCell({
          price: p?.annualFormatted,
          sub: p ? yearlySub(p.annualMonthlyFormatted, p.savingsPercentage) : "",
          available: p?.annualAvailable,
          differs: Boolean(a && p) && playDiffers(a.annualTotal, a.currencyCode, p.annualTotal, p.annualCurrency),
          applePrice: a?.annualFormatted,
          last: true
        })}
      </tr>`;
  }).join("");
}

async function handleSync({ functions, httpsCallable, showToast, callable, buttonId, textId, idleText, busyText, doneText }) {
  const button = document.getElementById(buttonId);
  if (button) button.disabled = true;
  setText(textId, busyText);

  try {
    const result = await httpsCallable(functions, callable)();
    if (result.data?.success) {
      showToast(doneText, "success");
    } else {
      showToast("Sync finished without new prices.", "info");
    }
    if (activeDependencies) {
      const { db, doc, getDoc } = activeDependencies;
      await fetchAndRenderPricing(db, doc, getDoc);
    }
  } catch (err) {
    console.error(`${callable} failed:`, err);
    showToast(`Sync failed: ${err.message}`, "error");
  } finally {
    if (button) button.disabled = false;
    setText(textId, idleText);
  }
}

function escapeHTML(str) {
  return String(str ?? "").replace(/[&<>'"]/g,
    tag => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[tag] || tag)
  );
}
